// The prompt for "Copy for agent": what to design, what to start from, and the rules for the result.
// The agent (Claude Code or any other) works in the same folder; the app reads what it writes.
import { serializeDocument, type EfModelDocument } from '@modelviewer/core';

export type AgentTask = 'alternatives' | 'refine' | 'review';

export interface PromptInput {
  task: AgentTask;
  /** Number of designs to propose (alternatives). */
  count: number;
  /** The use case in the user's words, or a note's text. */
  useCase: string;
  /** A note the use case comes from; a folder note is referred to by its path. */
  useCaseNote?: { title: string; path?: string };
  /** The model to start from. */
  base:
    | { kind: 'model'; context: string; migration: string | null }
    | { kind: 'sketch'; title: string; context: string | null; path?: string; doc?: EfModelDocument };
  /** Where the agent kit is installed, e.g. `.claude/skills/modelviewer`. */
  kit: string;
}

/** A file name from a title: lower case, dashes, at most 40 characters, cut between words. */
function slug(text: string): string {
  const full = text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const cut = full.length > 40 ? full.slice(0, 41).replace(/-[^-]*$/, '') : full;
  return cut || 'design';
}

export function buildAgentPrompt(input: PromptInput): string {
  const { base, kit } = input;
  const cli = `node ${kit}/scripts/modelviewer.mjs`;
  const context = base.context;
  const name = slug(input.useCaseNote?.title ?? input.useCase.split('\n')[0] ?? 'design');
  const out: string[] = [];

  out.push(`Use the modelviewer skill (${kit}/SKILL.md) to work on the EF Core data model of this repository.`, '');

  out.push('## Use case');
  if (input.useCaseNote?.path) out.push(`Described in \`${input.useCaseNote.path}\` ("${input.useCaseNote.title}"). Read it first.`);
  else out.push(input.useCase.trim() || '(Ask me what the design is for before you start.)');
  out.push('');

  out.push('## Start from');
  if (base.kind === 'model') {
    out.push(base.migration ? `The model of ${base.context} after the migration ${base.migration}: \`${cli} extract --context ${base.context} --migration ${base.migration}\`.` : `The current model of ${base.context}: \`${cli} extract --context ${base.context}\`.`);
  } else if (base.path) {
    out.push(`The sketch "${base.title}" in \`${base.path}\`.`);
  } else {
    out.push(`The sketch "${base.title}", which exists only in my browser, so it is included at the end of this message.`);
  }
  out.push('');

  out.push('## Task');
  if (input.task === 'alternatives') {
    out.push(
      `Propose ${input.count} different designs for this use case. Make them genuinely different (for example owned types versus separate entities, one table versus several, a JSON column versus rows), not small variations.`,
      `Save them as \`.modelviewer/sketches/${name}-<variant>.efmodel.json\`, and write \`.modelviewer/notes/${name}.md\` comparing them: how each serves the reads and writes of the use case, the trade-offs, and which you recommend.`,
    );
  } else if (input.task === 'refine') {
    out.push(
      base.kind === 'sketch' ? `Improve the sketch "${base.title}" for this use case.` : 'Improve the model for this use case.',
      `Save the result as a new file, \`.modelviewer/sketches/${name}-refined.efmodel.json\` (never overwrite a sketch you did not write in this task), and explain what you changed and why in its \`rationale\`.`,
    );
  } else {
    out.push(
      `Review ${base.kind === 'sketch' ? `the sketch "${base.title}"` : 'the model'} for this use case: what works, what will hurt (queries, integrity, migrations of existing data), and what you would change.`,
      `Write the review to \`.modelviewer/notes/${name}-review.md\`. Only add a sketch if a change is easier to show than to describe.`,
    );
  }
  out.push('');

  out.push('## Rules');
  out.push(
    `- Sketches are efmodel 1.0 documents. Set \`title\`, \`useCase\`, \`rationale\` and \`basedOn\` (${context ? `\`{ "context": "${context}"${base.kind === 'model' && base.migration ? `, "migration": "${base.migration}"` : ''}${base.kind === 'sketch' ? `, "sketch": "${base.title}"` : ''} }\`` : 'the context and migration you started from'}), and list every renamed entity or property under \`renames\` so the comparison pairs old and new names.`,
    '- In notes, link entities as [[Entity]] or [[Entity.Property]], designs as [[sketch:Title]] and migrations as [[migration:Name]].',
    `- Check every sketch: \`${cli} validate .modelviewer/sketches/<file>\` must report no errors. Run \`${cli} lint\` on it too, and say in the rationale which warnings you accept and why.`,
    `- See what a design changes with \`${cli} diff current .modelviewer/sketches/<file>\`.`,
    '- Keep to the logical model: leave out database-specific settings (column types, SQL defaults) unless the use case is about them.',
    '- Only create files under `.modelviewer/`. Do not change migrations, the model snapshot or any code.',
  );

  if (base.kind === 'sketch' && !base.path && base.doc) {
    out.push('', `## The sketch "${base.title}"`, '```json', serializeDocument(base.doc).trimEnd(), '```');
  }
  return `${out.join('\n')}\n`;
}
